/**
 * Tier Features Configuration
 * Defines which features are available at each subscription tier
 * 
 * TRIAL: All features unlocked for 30 days
 * After trial: Features lock based on tier
 */

// Feature code names mapped to display names
export const FEATURE_NAMES = {
  // Basic Features
  daily_signin: "Daily Sign-In/Out",
  child_management: "Child Management",
  emergency_report: "Emergency Contact Report",
  attendance_summary: "Attendance Summary Report",
  printing_download: "Printing & Download",
  holiday_calendar: "Smart Auto Updating Holiday & Events Calendar",
  smart_filters: "Smart Filters – Sort by Category / Child",
  school_calendar_sync: "Local School Calendars Sync",
  tour_ratio_planner: "Smart Center Tour Scheduling & Live Ratio Compliance",
  menu_planner: "Smart 12 Month Menu Planner",
  inventory_shopping_list: "Smart Menu, Inventory & Shopping List",
  feedback: "Feedback & Suggestions",

  // Premium Features
  bus_tracking: "A.M. Bus Tracking",
  center_alerts: "Real Time Center Alerts to Every Room",
  voice_input: "Voice Input",
  bulk_upload: "Bulk Upload/Delete",
  attendance_trends: "Attendance Trends",
  child_compliance_uploads: "Child Compliance Uploads",
  attendance_reports: "Attendance Reports",
  staff_calendar: "Staff Calendar",
  pickup_safety_email: "Pickup Safety Alerts (Email)",
  parent_sms_communications: "Parent SMS Communications",
  parent_communication_email: "Parent Communication Center Email",
  family_communications_hub: "Family Communications Hub",
  enrollment_forms: "Enrollment Forms",
  still_here_reminders: "Still Here Reminders",

  // Deluxe Features
  staff_compliance_tracker: "Staff & Child Compliance Report Tracker",
  compliance_dashboard: "Compliance Dashboard",
  inspection_mode: "Inspection Mode (30 Day Window)",
  meal_compliance: "Meal Compliance Tracker with Reports",
  infant_toddler_reports: "Infant/Toddler Reports",
  inspection_binder: "Generate Inspection Compliance Binder (16 items)",
  incident_reports: "Incident Reports",

  // Enterprise Features
  user_management: "User Management & Co-Owners",
  security_settings: "Security Settings",
  parent_portal: "Family SMS Access Portal",
  invoicing: "Invoicing",
  auto_invoicing: "Auto-Invoicing",
  payroll_hub: "Payroll Hub",
  full_backup: "Full Data Backup",

  // Signature (Owner Only)
  admin_customers: "Admin Customer View",
  feedback_admin: "Feedback Admin",
  unlimited_children: "Unlimited Children"
};

// Features included in each tier (cumulative - higher tiers include lower tier features)
export const TIER_FEATURES = {
  basic: [
    "daily_signin",
    "child_management",
    "emergency_report",
    "attendance_summary",
    "printing_download",
    "holiday_calendar",
    "smart_filters",
    "school_calendar_sync",
    "feedback"
  ],

  premium: [
    // All Basic features
    "daily_signin",
    "child_management",
    "emergency_report",
    "attendance_summary",
    "printing_download",
    "holiday_calendar",
    "smart_filters",
    "school_calendar_sync",
    "tour_ratio_planner",
    "menu_planner",
    "inventory_shopping_list",
    "feedback",
    // Premium additions
    "bus_tracking",
    "center_alerts",
    "voice_input",
    "bulk_upload",
    "attendance_trends",
    "child_compliance_uploads",
    "attendance_reports",
    "staff_calendar",
    "pickup_safety_email",
    "parent_sms_communications",
    "parent_communication_email",
    "family_communications_hub",
    "parent_portal",
    "enrollment_forms",
    "still_here_reminders",
    "staff_compliance_tracker",
    "infant_toddler_reports"
  ],

  deluxe: [
    // All Premium features
    "daily_signin",
    "child_management",
    "emergency_report",
    "attendance_summary",
    "printing_download",
    "holiday_calendar",
    "smart_filters",
    "school_calendar_sync",
    "tour_ratio_planner",
    "menu_planner",
    "inventory_shopping_list",
    "feedback",
    "bus_tracking",
    "center_alerts",
    "voice_input",
    "bulk_upload",
    "attendance_trends",
    "child_compliance_uploads",
    "attendance_reports",
    "staff_calendar",
    "pickup_safety_email",
    "parent_sms_communications",
    "parent_communication_email",
    "family_communications_hub",
    "parent_portal",
    "enrollment_forms",
    "still_here_reminders",
    "staff_compliance_tracker",
    "infant_toddler_reports",
    // Deluxe additions
    "user_management",
    "compliance_dashboard",
    "inspection_mode",
    "meal_compliance",
    "inspection_binder",
    "incident_reports"
  ],

  enterprise: [
    // All Deluxe features
    "daily_signin",
    "child_management",
    "emergency_report",
    "attendance_summary",
    "printing_download",
    "holiday_calendar",
    "smart_filters",
    "school_calendar_sync",
    "tour_ratio_planner",
    "menu_planner",
    "inventory_shopping_list",
    "feedback",
    "bus_tracking",
    "center_alerts",
    "voice_input",
    "bulk_upload",
    "attendance_trends",
    "child_compliance_uploads",
    "attendance_reports",
    "staff_calendar",
    "pickup_safety_email",
    "parent_sms_communications",
    "parent_communication_email",
    "family_communications_hub",
    "parent_portal",
    "enrollment_forms",
    "still_here_reminders",
    "staff_compliance_tracker",
    "compliance_dashboard",
    "inspection_mode",
    "meal_compliance",
    "infant_toddler_reports",
    "inspection_binder",
    "incident_reports",
    "full_backup",
    // Enterprise additions
    "user_management",
    "security_settings",
    "invoicing",
    "auto_invoicing",
    "payroll_hub"
  ],

  signature: [
    // All Enterprise features + Admin tools
    "daily_signin",
    "child_management",
    "emergency_report",
    "attendance_summary",
    "printing_download",
    "holiday_calendar",
    "smart_filters",
    "school_calendar_sync",
    "tour_ratio_planner",
    "menu_planner",
    "inventory_shopping_list",
    "feedback",
    "bus_tracking",
    "center_alerts",
    "voice_input",
    "bulk_upload",
    "attendance_trends",
    "child_compliance_uploads",
    "attendance_reports",
    "staff_calendar",
    "pickup_safety_email",
    "parent_sms_communications",
    "parent_communication_email",
    "family_communications_hub",
    "parent_portal",
    "enrollment_forms",
    "still_here_reminders",
    "staff_compliance_tracker",
    "compliance_dashboard",
    "inspection_mode",
    "meal_compliance",
    "infant_toddler_reports",
    "inspection_binder",
    "incident_reports",
    "full_backup",
    "user_management",
    "security_settings",
    "invoicing",
    "auto_invoicing",
    "payroll_hub",
    // Signature additions (owner only)
    "admin_customers",
    "feedback_admin",
    "unlimited_children"
  ],

  // Trial gets ALL features
  trial: [
    "daily_signin",
    "child_management",
    "emergency_report",
    "attendance_summary",
    "printing_download",
    "holiday_calendar",
    "smart_filters",
    "school_calendar_sync",
    "tour_ratio_planner",
    "menu_planner",
    "inventory_shopping_list",
    "feedback",
    "bus_tracking",
    "center_alerts",
    "voice_input",
    "bulk_upload",
    "attendance_trends",
    "child_compliance_uploads",
    "attendance_reports",
    "staff_calendar",
    "pickup_safety_email",
    "parent_sms_communications",
    "parent_communication_email",
    "family_communications_hub",
    "parent_portal",
    "enrollment_forms",
    "still_here_reminders",
    "staff_compliance_tracker",
    "compliance_dashboard",
    "inspection_mode",
    "meal_compliance",
    "infant_toddler_reports",
    "inspection_binder",
    "incident_reports",
    "full_backup",
    "user_management",
    "security_settings",
    "invoicing",
    "auto_invoicing",
    "payroll_hub"
  ]
};

// Minimum tier required for each feature
export const FEATURE_MINIMUM_TIER = {
  // Basic
  daily_signin: "basic",
  child_management: "basic",
  emergency_report: "basic",
  attendance_summary: "basic",
  printing_download: "basic",
  holiday_calendar: "basic",
  smart_filters: "basic",
  school_calendar_sync: "basic",
  feedback: "basic",

  // Premium
  tour_ratio_planner: "premium",
  menu_planner: "premium",
  inventory_shopping_list: "premium",
  bus_tracking: "premium",
  center_alerts: "premium",
  voice_input: "premium",
  bulk_upload: "premium",
  attendance_trends: "premium",
  child_compliance_uploads: "premium",
  attendance_reports: "premium",
  staff_calendar: "premium",
  pickup_safety_email: "premium",
  parent_sms_communications: "premium",
  parent_communication_email: "premium",
  family_communications_hub: "premium",
  enrollment_forms: "premium",
  still_here_reminders: "premium",
  staff_compliance_tracker: "premium",
  infant_toddler_reports: "premium",
  parent_portal: "premium",

  // Deluxe
  user_management: "deluxe",
  compliance_dashboard: "deluxe",
  inspection_mode: "deluxe",
  meal_compliance: "deluxe",
  inspection_binder: "deluxe",
  incident_reports: "deluxe",

  // Enterprise
  security_settings: "enterprise",
  invoicing: "enterprise",
  auto_invoicing: "enterprise",
  payroll_hub: "enterprise",
  full_backup: "enterprise",

  // Signature
  admin_customers: "signature",
  feedback_admin: "signature",
  unlimited_children: "signature"
};

// Tier display info
export const TIER_INFO = {
  basic: {
    name: "Basic",
    price: "$29/mo",
    maxChildren: 25,
    color: "amber"
  },
  premium: {
    name: "Premium",
    price: "$49/mo",
    maxChildren: 50,
    color: "blue"
  },
  deluxe: {
    name: "Premium Plus",
    price: "$79/mo",
    maxChildren: 90,
    color: "purple"
  },
  enterprise: {
    name: "Enterprise",
    price: "$99/mo",
    maxChildren: 125,
    color: "green"
  },
  signature: {
    name: "Signature",
    price: "Custom",
    maxChildren: "Unlimited",
    color: "indigo"
  }
};

// Tier order for comparison
export const TIER_ORDER = ["basic", "premium", "deluxe", "enterprise", "signature"];

/**
 * Check if a tier is the Signature tier (owner-only, unlimited access)
 */
export const isSignatureTier = (tier) => {
  return tier?.toLowerCase() === 'signature';
};

/**
 * Check if a tier has unlimited children access
 */
export const hasUnlimitedChildren = (tier) => {
  return isSignatureTier(tier);
};

/**
 * Check if a tier has access to a feature
 * SIGNATURE TIER: Always has access to ALL features, no exceptions
 */
export const tierHasFeature = (tier, feature) => {
  const normalizedTier = tier?.toLowerCase();
  
  // SIGNATURE TIER: Unconditional access to ALL features
  if (normalizedTier === 'signature') {
    return true;
  }
  
  // TRIAL: Also gets all features during trial period
  if (normalizedTier === 'trial') {
    return true;
  }
  
  // EXPIRED: Trial ended without upgrade → no feature access until they pick a plan
  if (normalizedTier === 'expired') {
    return false;
  }
  
  // Other tiers: check feature list
  const tierFeatures = TIER_FEATURES[normalizedTier] || TIER_FEATURES.signature;
  return tierFeatures.includes(feature);
};

/**
 * Get the minimum tier required for a feature
 */
export const getMinimumTier = (feature) => {
  return FEATURE_MINIMUM_TIER[feature] || "basic";
};

/**
 * Check if current tier is >= required tier
 */
export const isTierSufficient = (currentTier, requiredTier) => {
  // CRITICAL: signature tier is always sufficient
  const normalizedCurrent = currentTier?.toLowerCase();
  if (normalizedCurrent === 'signature') {
    return true;
  }
  const currentIndex = TIER_ORDER.indexOf(normalizedCurrent || "signature"); // Default to signature (full access)
  const requiredIndex = TIER_ORDER.indexOf(requiredTier?.toLowerCase() || "basic");
  return currentIndex >= requiredIndex;
};
